/**
 * Cart drawer "Upgrade to subscription" control.
 *
 * Converts an eligible one-time cart line into a subscription (and lets the
 * customer adjust delivery frequency afterwards) by calling /cart/change.js
 * with the line key + a Recharge selling plan.
 *
 * Relies on the shared parser in assets/subscription-plans.js and the theme
 * globals: routes, fetchConfig, publish, PUB_SUB_EVENTS, checkAndAddGWP,
 *          applyMilestoneGiftPlanInline.
 */
class CartSubscriptionUpgrade extends HTMLElement {
  connectedCallback() {
    this._lineKey = this.dataset.lineKey;
    this._quantity = parseInt(this.dataset.quantity, 10) || 1;
    this._currentPlanId = this.dataset.currentSellingPlanId
      ? String(this.dataset.currentSellingPlanId)
      : '';
    this._variantPrice = parseInt(this.dataset.variantPrice, 10) || 0;
    this._variantCompareAt = parseInt(this.dataset.variantCompareAtPrice, 10) || 0;
    this._lineDiscounted = this.dataset.lineDiscounted === 'true';

    this._button = this.querySelector('[data-role="upgrade-button"]');
    this._label = this.querySelector('[data-role="upgrade-label"]');
    this._freqWrapper = this.querySelector('[data-role="frequency-wrapper"]');
    this._freqSelect = this.querySelector('[data-role="frequency-select"]');

    this._plans = this._parsePlans();
    this._frequencies =
      window.SubscriptionPlans && window.SubscriptionPlans.buildFrequencies
        ? window.SubscriptionPlans.buildFrequencies(this._plans)
        : [];

    if (this._frequencies.length === 0) return; // No recognizable plans.

    if (this._currentPlanId) {
      this._renderSubscribedState();
    } else {
      this._renderOneTimeState();
    }
  }

  /**
   * Saving against RRP: the sale discount already in the line price plus the
   * subscription discount on top. Mirrors the PDP widget so the same product
   * quotes the same percentage in both places. Falls back to the plan discount
   * when the variant has no compare-at price.
   *
   * 0 while a discount code or automatic discount applies to the line: the
   * figure would be measured against a price the customer isn't paying, and the
   * discount may not carry over to a subscription, so the copy drops the number.
   */
  _savingPercent(discountPct) {
    if (this._lineDiscounted) return 0;
    const pct = discountPct || 0;
    const base = this._variantPrice;
    const rrp = this._variantCompareAt > base ? this._variantCompareAt : base;
    if (!base || !rrp) return pct;
    const pay = Math.round(base * (1 - pct / 100));
    return ((rrp - pay) / rrp) * 100;
  }

  _parsePlans() {
    const script = this.querySelector('script[data-selling-plans]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent) || [];
    } catch (e) {
      console.error('[cart-subscription-upgrade] Failed to parse selling plans', e);
      return [];
    }
  }

  /**
   * Resolve the plan covering `qty` in a bucket. Goes through the shared helper
   * so a flat (quantity-less) plan resolves as a wildcard for every quantity —
   * reaching into plansByQty directly would find nothing once plans stop naming
   * a quantity.
   */
  _bucketPlan(bucket, qty) {
    if (window.SubscriptionPlans && typeof window.SubscriptionPlans.planForQuantity === 'function') {
      return window.SubscriptionPlans.planForQuantity(bucket, qty);
    }
    if (!bucket) return null;
    return (bucket.plansByQty && bucket.plansByQty[qty]) || bucket.anyQtyPlan || null;
  }

  /** Frequencies (sorted shortest-first) that offer a plan for this line's quantity. */
  _frequenciesForQuantity() {
    return this._frequencies.filter((f) => this._bucketPlan(f, this._quantity));
  }

  /** Plan for a given quantity at the shortest frequency offering it (or null). */
  _planForQuantity(qty) {
    const f = this._frequencies.find((fr) => this._bucketPlan(fr, qty));
    return f ? this._bucketPlan(f, qty) : null;
  }

  /**
   * The largest configured quantity tier's plan (shortest frequency).
   *
   * Only meaningful for quantity-upsell plans. A flat plan has no tiers, so this
   * returns null — but it is only ever a fallback for quantities the tiers do
   * not reach, and a flat plan reaches all of them.
   */
  _maxTierPlan() {
    let maxQty = 0;
    this._frequencies.forEach((f) => {
      Object.keys(f.plansByQty || {}).forEach((q) => {
        const n = parseInt(q, 10);
        if (n > maxQty) maxQty = n;
      });
    });
    return maxQty ? this._planForQuantity(maxQty) : null;
  }

  _renderOneTimeState() {
    // Plan for this quantity, or — if the quantity is beyond the configured
    // tiers — the maximum tier plan so we still offer (and keep) the best saving.
    const plan = this._planForQuantity(this._quantity) || this._maxTierPlan();
    if (!plan) return; // No recognizable plans at all.
    this._defaultPlanId = plan.id;

    if (this._label) {
      const pct = this._savingPercent(plan.discountPercent);
      this._label.textContent = pct > 0 ? `Click to subscribe & save ${this._formatPercent(pct)}` : 'Click to subscribe & save';
    }
    if (this._button) {
      this._button.hidden = false;
      this._button.addEventListener('click', (e) => {
        e.preventDefault();
        this._applyPlan(this._defaultPlanId, this._button);
      });
    }
  }

  _renderSubscribedState() {
    // Which frequency bucket does the current plan belong to (by id, any qty)?
    const currentBucket = this._frequencies.find((f) => {
      const plans = Object.values(f.plansByQty || {});
      if (f.anyQtyPlan) plans.push(f.anyQtyPlan);
      return plans.some((p) => String(p.id) === this._currentPlanId);
    });
    if (!currentBucket) return; // Unrecognized plan — leave untouched.

    const available = this._frequenciesForQuantity();

    // Quantity beyond the available subscription tiers: keep the subscription
    // on the maximum tier plan so the customer retains the best saving.
    if (available.length === 0) {
      const maxPlan = this._maxTierPlan();
      if (maxPlan && String(maxPlan.id) !== this._currentPlanId) {
        this._applyPlan(maxPlan.id);
      }
      return;
    }

    // Quantity-upsell plans encode the quantity tier. If the line quantity no
    // longer matches the plan's tier (e.g. the native quantity stepper changed
    // it), auto-correct to the plan for this quantity — preferring the current
    // frequency, otherwise the shortest interval available.
    const tierPlan =
      this._bucketPlan(currentBucket, this._quantity) ||
      this._bucketPlan(available[0], this._quantity);
    if (tierPlan && String(tierPlan.id) !== this._currentPlanId) {
      this._applyPlan(tierPlan.id);
      return;
    }

    // Build the frequency dropdown from frequencies that offer this quantity.
    if (!this._freqSelect || available.length <= 1) return;

    this._freqSelect.innerHTML = '';
    available.forEach((f) => {
      const p = this._bucketPlan(f, this._quantity);
      const opt = document.createElement('option');
      opt.value = String(p.id);
      const savingPct = this._savingPercent(p.discountPercent);
      opt.textContent = savingPct > 0 ? `${f.label} (${this._formatPercent(savingPct)} off)` : f.label;
      this._freqSelect.appendChild(opt);
    });
    this._freqSelect.value = this._currentPlanId;
    this._freqSelect.addEventListener('change', () => {
      const planId = this._freqSelect.value;
      if (planId && planId !== this._currentPlanId) {
        this._applyPlan(planId, null, this._freqSelect);
      }
    });
    if (this._freqWrapper) this._freqWrapper.hidden = false;
  }

  /**
   * Apply a selling plan to this cart line via /cart/change.js, then re-render
   * the cart drawer using the theme's existing render flow.
   */
  _applyPlan(planId, button, select) {
    if (this._loading) return;
    this._loading = true;
    this._setLoading(button, select, true);

    const cartDrawer = document.querySelector('cart-drawer');
    const sectionIds = cartDrawer ? cartDrawer.getSectionsToRender().map((s) => s.id) : [];

    const body = JSON.stringify({
      id: this._lineKey,
      quantity: this._quantity,
      // null removes the selling plan (converts the line back to one-time).
      selling_plan: planId || null,
      sections: sectionIds,
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status || parsedState.errors) {
          this._showError(parsedState.message || window.cartStrings?.error || 'Could not update the cart.');
          this._setLoading(button, select, false);
          this._loading = false;
          return;
        }

        // Switching to a subscription is exactly what earns the subscription
        // gift, so settle the gifts before painting: the gift then arrives in
        // the same render as the plan change, not a beat after it. The
        // drawer-items element supplies the section list, which is the same
        // pair requested above. Falls back to the plain state when cart.js
        // has not loaded (it is deferred too).
        const cartDrawerItems = document.querySelector('cart-drawer-items');
        const renderState =
          cartDrawerItems && typeof applyMilestoneGiftPlanInline === 'function'
            ? await applyMilestoneGiftPlanInline(parsedState, cartDrawerItems)
            : parsedState;

        // Render the drawer ourselves. Use source 'cart-items' so the drawer's
        // own cartUpdate listener (cart.js) skips its redundant refetch, which
        // would otherwise race with renderContents and cause flicker/breakage.
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.renderContents(renderState);
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'cart-items',
            cartData: renderState,
            variantId: this.dataset.variantId,
          });
        }

        // Only needed when the inline pass could not run; otherwise the gifts
        // are already settled and this would be a no-op fetch.
        if (renderState === parsedState && typeof checkAndAddGWP === 'function') {
          checkAndAddGWP(parsedState);
        }
        // A successful render replaces this element, so no need to reset state.
      })
      .catch((e) => {
        console.error('[cart-subscription-upgrade]', e);
        this._showError(window.cartStrings?.error || 'Could not update the cart.');
        this._setLoading(button, select, false);
        this._loading = false;
      });
  }

  _setLoading(button, select, isLoading) {
    if (button) {
      button.classList.toggle('loading', isLoading);
      button.setAttribute('aria-disabled', isLoading ? 'true' : 'false');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
    }
    if (select) select.disabled = isLoading;
  }

  _showError(message) {
    const errors = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (errors) errors.textContent = message;
  }

  _formatPercent(value) {
    if (Number.isInteger(value)) return `${value}%`;
    return `${parseFloat(value).toFixed(1).replace(/\.0$/, '')}%`;
  }
}

if (!customElements.get('cart-subscription-upgrade')) {
  customElements.define('cart-subscription-upgrade', CartSubscriptionUpgrade);
}
